// client/src/pages/Admin/ResourceManager/hooks/useResourceFilters.ts
import { useState, useMemo, useCallback } from 'react';
import { Resource } from '../../../../shared/models/Resource';
import { FilterState } from '../types/resource.types';
import { DEFAULT_FILTERS } from '../constants/resource.constants';
import { getParentCategory } from '../utils/resourceHelpers';
import { resourceMatchesSearch } from '../utils/resourceSearch';

export const useResourceFilters = (resources: Resource[], externalDebouncedSearchTerm?: string) => {
    const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

    const activeDebouncedSearchTerm = externalDebouncedSearchTerm ?? debouncedSearchTerm;

    const filteredResources = useMemo((): Resource[] => {
        let filtered = resources;

        if (activeDebouncedSearchTerm) {
            filtered = filtered.filter((r: Resource) => 
                resourceMatchesSearch(r, activeDebouncedSearchTerm).matches
            );
        }

        if (filters.status !== 'all') {
            filtered = filtered.filter((r: Resource) => r.status === filters.status);
        }

        if (filters.parentCategory !== 'all') {
            filtered = filtered.filter((r: Resource) => 
                getParentCategory(r.category) === filters.parentCategory
            );
        }

        if (filters.category !== 'all') {
            filtered = filtered.filter((r: Resource) => r.category === filters.category);
        }

        if (filters.featured !== 'all') {
            const isFeatured = filters.featured === 'true';
            filtered = filtered.filter((r: Resource) => r.isFeatured === isFeatured);
        }

        if (filters.author !== 'all') {
            filtered = filtered.filter((r: Resource) => 
                (r.authors ?? []).some((author: { name?: string }) => 
                    author.name === filters.author
                )
            );
        }

        if (filters.year !== 'all') {
            filtered = filtered.filter((r: Resource) => {
                if (!r.publishedDate) return false;
                try {
                    const year = new Date(r.publishedDate).getFullYear();
                    return year.toString() === filters.year;
                } catch {
                    return false;
                }
            });
        }

        return filtered;
    }, [resources, activeDebouncedSearchTerm, filters]);

    const filterResources = useCallback((items: Resource[]): Resource[] => {
        return filteredResources.filter((resource) => items.includes(resource));
    }, [filteredResources]);

    const hasActiveFilters = useMemo(() => {
        return Object.values(filters).some(value => value !== 'all') || searchTerm !== '';
    }, [filters, searchTerm]);

    const activeFilterCount = useMemo(() => {
        return Object.values(filters).filter(value => value !== 'all').length;
    }, [filters]);

    const handleFilterChange = useCallback((key: string, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    const updateFilter = useCallback((key: string, value: string) => {
        handleFilterChange(key, value);
    }, [handleFilterChange]);

    const resetFilters = useCallback(() => {
        setFilters(DEFAULT_FILTERS);
        setSearchTerm('');
        setDebouncedSearchTerm('');
    }, []);

    const clearSearch = useCallback(() => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
    }, []);

    return {
        filters,
        searchTerm,
        debouncedSearchTerm,
        filteredResources,
        filterResources,
        hasActiveFilters,
        activeFilterCount,
        setSearchTerm,
        setDebouncedSearchTerm,
        handleFilterChange,
        updateFilter,
        resetFilters,
        clearSearch
    };
};